/**
 * Return a path that can be used for same-origin navigation after login.
 *
 * Login redirects are carried in a query parameter, so they are user input.
 * Only a relative path is accepted. In particular, protocol-relative URLs and
 * backslashes are rejected because browsers may normalize them into external
 * origins before navigation.
 */
export function getSafeInternalRedirect(value: unknown, origin = globalThis.location.origin): string | null {
  const hasControlCharacter = (input: string) => {
    for (const character of input) {
      const codePoint = character.codePointAt(0) ?? 0;
      if (codePoint < 0x20 || codePoint === 0x7F) return true;
    }

    return false;
  };

  if (
    typeof value !== "string" ||
    value.length === 0 ||
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value.includes("\\") ||
    hasControlCharacter(value)
  ) {
    return null;
  }

  try {
    const url = new URL(value, origin);
    if (url.origin !== origin) return null;

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

/** Resolve the destination after login from same-origin paths only. */
export function getLoginDestination(queryRedirect: unknown, guardedRedirect: unknown): string {
  return getSafeInternalRedirect(queryRedirect)
    ?? getSafeInternalRedirect(guardedRedirect)
    ?? "/";
}
